import { Wizard, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import {
  CreateEquipmentDocument,
  ItAssetsStatusCountsDocument,
  ItAssetsTableDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { formatDateForGraphQL } from 'modules/it_management/lib/dateUtils'

import AssignmentStep from './AssignmentStep'
import DetailsStep from './DetailsStep'
import { WizardData } from './types'

interface NewEquipmentModalProps {
  onClose: () => void
}

export const NewEquipmentModal = ({ onClose }: NewEquipmentModalProps) => {
  const [createEquipment] = useMutation(CreateEquipmentDocument, {
    extractorFn: (data) => data.itManagementMutations.importItmanagementItAsset,
    awaitRefetchQueries: true,
    refetchQueries: [ItAssetsTableDocument, ItAssetsStatusCountsDocument],
  })

  const handleSubmit = async (data: WizardData) => {
    track(trackingEvents.itManagement.newDevice.createEquipmentSubmit)
    app.setProgress({
      message: i18n.t('it_management.new_equipment_modal.messages.creating_equipment'),
    })

    const result = await createEquipment({
      variables: {
        serialNumber: data.serialNumber || '',
        label: data.label || '',
        notes: data.notes || '',
        typeName: data.assetModelType || '',
        brand: data.assetModelBrand || '',
        name: data.assetModelName || '',
        ownerId: data.employeeId ?? undefined,
        locationId: data.locationId ? parseInt(data.locationId, 10) : undefined,
        teamId: data.teamId ? parseInt(data.teamId, 10) : undefined,
        workplaceId: data.workplaceId ? parseInt(data.workplaceId, 10) : undefined,
        purchaseDate: formatDateForGraphQL(data.purchaseDate),
        purchasePriceCents: data.purchasePrice
          ? Math.round(parseFloat(data.purchasePrice) * 100)
          : undefined,
        warrantyEndDate: formatDateForGraphQL(data.warrantyEndDate),
        currency: data.currency || 'EUR',
      },
    })

    app.setProgress(null)

    const handled = handleResult(result, ({ warrantyEndDate }) => {
      track(trackingEvents.itManagement.newDevice.error)
      app.setFlash({
        type: 'wrong',
        message:
          warrantyEndDate ||
          i18n.t('it_management.new_equipment_modal.messages.failed_to_create_equipment'),
      })
    })

    if (handled.success) {
      track(trackingEvents.itManagement.newDevice.success)
      app.setFlash({
        type: 'correct',
        message: i18n.t(
          'it_management.new_equipment_modal.messages.equipment_created_successfully'
        ),
      })
      onClose()
    }

    track(trackingEvents.accounting.openNewContactModal, {
      equipment_name: data.assetModelName,
      equipment_brand: data.assetModelBrand,
      equipment_type: data.assetModelType,
      has_serial_number: !!data.serialNumber,
      assignment_type: data.assignmentType,
      has_assignment: !!data.employeeId || !!data.workplaceId || !!data.teamId || !!data.locationId,
      has_location: !!data.locationId,
    })
  }

  const isDetailsCompleted = (data: WizardData): boolean => {
    return !!data.assetModelName && !!data.assetModelBrand && !!data.assetModelType
  }

  const initialData: WizardData = {
    assetModelName: '',
    assetModelBrand: '',
    assetModelType: 'laptop',
    serialNumber: '',
    label: undefined,
    notes: undefined,
    purchaseDate: undefined,
    purchasePrice: undefined,
    warrantyEndDate: undefined,
    currency: 'EUR',
    assignmentType: 'person',
    employeeId: null,
    workplaceId: undefined,
    workplaceName: undefined,
    teamId: undefined,
    teamName: undefined,
    locationId: undefined,
    locationName: undefined,
    isSaveDisabled: false,
  }

  return (
    <Wizard.Modal<WizardData>
      size="large"
      initialData={initialData}
      icon="laptop-user"
      title={i18n.t('it_management.new_equipment_modal.title')}
      onClose={onClose}
      onSubmit={handleSubmit}
      steps={[
        {
          title: i18n.t('it_management.new_equipment_modal.steps.equipment_details.title'),
          nextText: i18n.t('it_management.new_equipment_modal.steps.equipment_details.next_text'),
          render: (props: WizardStepRenderParams<WizardData>) => <DetailsStep {...props} />,
          isCompleted: isDetailsCompleted,
        },
        {
          title: i18n.t('it_management.new_equipment_modal.steps.assignation.title'),
          nextText: i18n.t('it_management.new_equipment_modal.steps.assignation.next_text'),
          render: (props: WizardStepRenderParams<WizardData>) => <AssignmentStep {...props} />,
          isCompleted: (data: WizardData) => !data.isSaveDisabled,
        },
      ]}
    />
  )
}
